
import { Vue, Prop, Component } from "vue-property-decorator";
import DateTimePicker from "@/components/DatetimePicker.vue";
import Passenger from "@/components/Passenger/models/Passenger";
import { RolePermission } from "@/models/Role";
import Trip from "@/components/Passenger/models/Trip";
import RouteWithTrips from "../models/RouteWithTrips";
import RouteDetails from "@/components/RouteDetails.vue";
import { DateHelper } from '@/util/DateHelper';
import restService from "@/services/restService";
import moment from "moment";
import vuetifyToast from "vuetify-toast";

@Component({
  components: {
    DateTimePicker,
    RouteDetails,
  }
})
export class PassengerTripList extends Vue {
  tripsStartDate: string = new Date().toISOString().substr(0, 10);
  tripsEndDate: string = new Date().toISOString().substr(0, 10);
  selectAllTripsStatus: boolean = false;
  tripsHaveBeenSearched: boolean = false;
  showCancelDialog: boolean = false;
  cancelReason: string = '';
  cancelReasonRules: any[] = [val => this.cancelReasonRequired(val)];
  cancelReasonValid = false;
  selectedTripId: number | null = null;
  selectedRoute: RouteWithTrips | null = null;
  $dateHelper: DateHelper;

  loadingRouteDetails = false;

  @Prop({ default: null }) passenger: Passenger;

  get userCanReadPassengers(): boolean {
    return this.$store.getters['auth/hasPassengerPermission']([RolePermission.ReadPassengers]);
  }

  get userCanUpdatePassengers(): boolean {
    return this.$store.getters['auth/hasPassengerPermission']([RolePermission.UpdatePassengers]);
  }

  get destinationId(): number {
    if (this.passenger && this.passenger.destinationIds.length > 0) {
      return this.passenger.destinationIds.find(d => d);
    }
    return 0;
  }

  get tripHeaders(): any[] {
    return [
      {
        text: this.$i18n.translate("passenger.prop.trips.startPlace", "Start place"),
        align: "left",
        sortable: false,
        value: "startPlace"
      },
      {
        text: this.$i18n.translate("passenger.prop.trips.endPlace", "End place"),
        align: "left",
        sortable: false,
        value: "endPlace"
      },
      {
        text: this.$i18n.translate("passenger.prop.trips.startTime", "Start time"),
        align: "left",
        sortable: false,
        value: "startDateTime"
      },
      {
        text: this.$i18n.translate("passenger.prop.trips.endTime", "End time"),
        align: "left",
        sortable: false,
        value: "endDateTime"
      },
      {
        text: this.$i18n.translate("passenger.prop.trips.cancelledAt", "Cancelled at"),
        align: "left",
        sortable: false,
        value: "cancelledAt"
      }
    ];
  }

  get disableSearchTrips() {
    if (this.loadingTrips) {
      return true;
    }
    if (!this.passenger.movitId) {
      return true;
    }
    if (!this.userCanReadPassengers) {
      return true;
    }
    if (this.passenger.inactive) {
      return true;
    }
    if (!this.destinationId) {
      return true;
    }
    return false;
  }

  get disableCancelTrips() {
    if (this.disableSearchTrips) {
      return true;
    }
    if (this.selectedTripsCount === 0) {
      return true;
    }
    return false;
  }

  get loadingTrips(): boolean {
    return this.$store.state.passenger.pending.getPassengerTrips;
  }

  get sortedTrips(): Trip[] {
    const tripArray = this.$store.state.passenger.trips;

    tripArray.sort((a, b) => {
      const dateA = new Date(a.startDateTime ? a.startDateTime : a.endDateTime);
      const dateB = new Date(b.startDateTime ? b.startDateTime : b.endDateTime);
      return dateA <= dateB;
    });

    return tripArray;
  }

  get selectedTripsCount() {
    const selection = this.$store.state.passenger.trips.filter(
      t => t.selectedForCancelling
    );
    return selection.length;
  }

  startDateUpdated(value) {
    this.tripsStartDate = value;
    if (value > this.tripsEndDate) {
      this.tripsEndDate = value;
    }
    const maxEnd = new Date(value);
    maxEnd.setMonth(maxEnd.getMonth() + 1);
    if (new Date(this.tripsEndDate).getTime() > maxEnd.getTime()) {
      this.tripsEndDate = maxEnd.toISOString().substr(0, 10);
    }
  }

  endDateUpdated(value) {
    this.tripsEndDate = value;
    if (value < this.tripsStartDate) {
      this.tripsStartDate = value;
    }
    const minStart = new Date(value);
    minStart.setMonth(minStart.getMonth() - 1);
    if (new Date(this.tripsStartDate).getTime() < minStart.getTime()) {
      this.tripsStartDate = minStart.toISOString().substr(0, 10);
    }
  }

  async getPassengerTrips() {
    if (!this.passenger.movitId || !this.destinationId) {
      return;
    }

    await this.$store.dispatch("passenger/getPassengerTrips", {
      params: {
        passengerId: this.passenger.id,
        startDate: this.tripsStartDate,
        endDate: this.tripsEndDate,
        destinationId: !!this.destinationId ? this.destinationId : 0,
      }
    });
    this.tripsHaveBeenSearched = true;
    this.clearSelections();
  }

  forceSelectionForTrips(value) {
    this.sortedTrips.forEach(t => {
      if (!t.cancelledAt) {
        t.selectedForCancelling = value;
      }
    });
  }

  clearSelections() {
    this.selectAllTripsStatus = false;
    this.sortedTrips.forEach(t => (t.selectedForCancelling = false));
  }

  toggleItemPicked(trip: Trip, value) {
    trip.selectedForCancelling = value;
    this.sortedTrips.find(t => t.id === trip.id);
  }

  toggleAllPicked() {
    if (this.selectAllTripsStatus === true) {
      this.forceSelectionForTrips(false);
      this.selectAllTripsStatus = false;
    } else {
      this.forceSelectionForTrips(true);
      this.selectAllTripsStatus = true;
    }
  }

  cancelReasonRequired(val: string) {
    return !!val || Vue.i18n.translate('validation.rules.required', 'Required');
  }

  async onCancelTripsClick() {
    const toBeCancelled = this.sortedTrips.filter(
      t => t.selectedForCancelling === true
    );
    const cancellation = {
      reason: this.cancelReason,
      trips: toBeCancelled
    };
    await this.$store
      .dispatch("passenger/cancelTrips", {
        params: {
          passengerId: this.passenger.id
        },
        data: cancellation
      })
      .then(result =>
        this.getPassengerTrips()
          .finally
          // this.getPassengerTrips()
          ()
      );

    this.showCancelDialog = false;
  }

  async openRouteDetails(trip: Trip) {
    
    this.selectedRoute = await this.getRouteDetailsForTrip(trip);
    if (!this.selectedRoute) {
      vuetifyToast.info(this.$t('routeDetails.tripHasNoRoute'));
      return;
    }
    this.selectedTripId = trip.id;
  }

  async updateRouteDetails(trip: Trip) {
    if (!trip) {
      return;
    }
    this.selectedRoute = await this.getRouteDetailsForTrip(trip);
  }

  async getRouteDetailsForTrip(trip: Trip) {
    const tripDate = moment(trip.startDateTime || trip.endDateTime).format("YYYY-MM-DD");
    try {
      this.loadingRouteDetails = true;
      return await restService.passengers.getPassengerRouteWithTrips(this.passenger.id, trip.id, tripDate);
    } finally {
      this.loadingRouteDetails = false;
    }
  }

  closeRouteDetails() {
    this.selectedRoute = null;
    this.selectedTripId = null;
  }

  mounted() {
    this.tripsHaveBeenSearched = false;
  }
}

export default PassengerTripList;
